import { Ulid } from "@kase/my-marathon-communication";
import { ApiSliceParams } from "../interfaces";
import { hasPatchChanges } from "../utils";
import {
    Budget,
    BudgetFeeLine,
    BudgetFeeLineUpdateRequest,
    BudgetLine,
    BudgetOtherLine,
    BudgetOtherLineUpdateRequest,
    BudgetPurchaseLine,
    BudgetPurchaseLineUpdateRequest,
} from "./interfaces";

// A - fee
// I - purchase
// O - other
// P - phase
//
export const makeLine = ({ connection, company }: ApiSliceParams) => {
    const updateFee = async (
        record: BudgetFeeLine,
        params: BudgetFeeLineUpdateRequest,
    ) => {
        const con = connection.value;
        const changed = hasPatchChanges(record.data, params, true);

        if (!changed) {
            return false;
        }

        await con.budget.budgetline.updateA({
            id: record._id,
            company: company.value,
            lineA: params,
        });

        return true;
    };

    const updatePurchase = async (
        record: BudgetPurchaseLine,
        params: BudgetPurchaseLineUpdateRequest,
    ) => {
        const con = connection.value;
        const changed = hasPatchChanges(record.data, params, true);

        if (!changed) {
            return false;
        }

        await con.budget.budgetline.updateI({
            id: record._id,
            company: company.value,
            lineI: params,
        });

        return true;
    };

    const updateOther = async (
        record: BudgetOtherLine,
        params: BudgetOtherLineUpdateRequest,
    ) => {
        const con = connection.value;
        const changed = hasPatchChanges(record.data, params, true);

        if (!changed) {
            return false;
        }

        await con.budget.budgetline.updateO({
            id: record._id,
            company: company.value,
            lineO: params,
        });

        return true;
    };

    const move = async (
        records: BudgetLine[],
        budget: Budget,
        target: Readonly<Ulid>,
    ) => {
        const con = connection.value;
        const phase = budget.phases.peek().find((p) => p._id.equals(target));
        const offset = phase?.lines.peek().at(-1)?.data.position ?? 0;
        const promises: Promise<unknown>[] = [];
        let position = offset + 1;

        for (const line of records) {
            if (line.t === "A") {
                promises.push(
                    con.budget.budgetline.updateA({
                        company: company.value,
                        id: line._id,
                        lineA: {
                            phase: target,
                            position: position++,
                        },
                    }),
                );
            } else if (line.t === "I") {
                promises.push(
                    con.budget.budgetline.updateI({
                        company: company.value,
                        id: line._id,
                        lineI: {
                            phase: target,
                            position: position++,
                        },
                    }),
                );
            } else if (line.t === "O") {
                promises.push(
                    con.budget.budgetline.updateO({
                        company: company.value,
                        id: line._id,
                        lineO: {
                            phase: target,
                            position: position++,
                        },
                    }),
                );
            }
        }

        await Promise.allSettled(promises);
    };

    const copy = async (
        records: BudgetLine[],
        budget: Budget,
        target: Readonly<Ulid>,
    ) => {
        const con = connection.value;
        const phase = budget.phases.peek().find((p) => p._id.equals(target));
        const offset = phase?.lines.peek().at(-1)?.data.position ?? 0;
        const promises: Promise<unknown>[] = [];
        let position = offset + 1;

        for (const line of records) {
            if (line.t === "A") {
                promises.push(
                    con.budget.budgetline.createA({
                        company: company.value,
                        budget: budget._id,
                        lineA: {
                            ...line.data,
                            position: position++,
                            phase: target,
                        },
                    }),
                );
            } else if (line.t === "I") {
                promises.push(
                    con.budget.budgetline.createI({
                        company: company.value,
                        budget: budget._id,
                        lineI: {
                            ...line.data,
                            position: position++,
                            phase: target,
                        },
                    }),
                );
            } else if (line.t === "O") {
                promises.push(
                    con.budget.budgetline.createO({
                        company: company.value,
                        budget: budget._id,
                        lineO: {
                            ...line.data,
                            position: position++,
                            phase: target,
                        },
                    }),
                );
            }
        }

        await Promise.allSettled(promises);
    };

    return { updateFee, updatePurchase, updateOther, move, copy };
};
